import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Ingredients from "@/pages/Ingredients";
import Recipes from "@/pages/Recipes";
import Pricing from "@/pages/Pricing";
import Payroll from "@/pages/Payroll";
import Expenses from "@/pages/Expenses";
import Dre from "@/pages/Dre";
import BreakEven from "@/pages/BreakEven";
import Losses from "@/pages/Losses";
import Combos from "@/pages/Combos";
import Shopping from "@/pages/Shopping";
import Activate, { AuthCallback } from "@/pages/Activate";
import AdminAccess from "@/pages/AdminAccess";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
    <div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
  </div>
);

const Protected = () => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_done && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return <Outlet />;
};

const Public = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={user.onboarding_done ? "/" : "/onboarding"} replace />;
  return <Outlet />;
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route element={<Public />}>
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Register />} />
              <Route path="/esqueci-senha" element={<ForgotPassword />} />
            </Route>
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/ativar" element={<Activate />} />
            <Route element={<Protected />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route element={<Layout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/insumos" element={<Ingredients />} />
                <Route path="/fichas" element={<Recipes />} />
                <Route path="/combos" element={<Combos />} />
                <Route path="/compras" element={<Shopping />} />
                <Route path="/precificacao" element={<Pricing />} />
                <Route path="/folha" element={<Payroll />} />
                <Route path="/despesas" element={<Expenses />} />
                <Route path="/dre" element={<Dre />} />
                <Route path="/ponto-equilibrio" element={<BreakEven />} />
                <Route path="/perdas" element={<Losses />} />
                <Route path="/admin/acessos" element={<AdminAccess />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
